import { PERSONAL_TAG_SCOPE } from '../lib/tagScope';
import { apiFetch } from './client';

export const CUSTOMER_PANEL_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;
export type CustomerPanelPriority = (typeof CUSTOMER_PANEL_PRIORITIES)[number];

export interface CustomerPanelContact {
  id: string;
  displayName: string | null;
  whatsappProfileName?: string | null;
  phone: string;
  email: string | null;
  language: string | null;
  city: string | null;
  country: string | null;
  customerSince: string;
  sessionActive: boolean;
}

export interface CustomerPanelOwner {
  id: string;
  name: string;
  initials: string;
}

export interface CustomerPanelDepartment {
  id: string;
  name: string;
}

export interface CustomerPanelConversation {
  ticketId: string;
  department: CustomerPanelDepartment | null;
  priority: CustomerPanelPriority;
}

export interface CustomerPanelTag {
  id: string;
  name: string;
  color: string | null;
}

/**
 * Tags are PERSONAL: the server returns only the signed-in account's own tags, and marks every
 * such response with this scope. Anything without the marker (an older server that still serves
 * shared tags) is never shown as "my tags": the tag APIs below reject it, and callers show the
 * tags as unavailable instead.
 */
export { PERSONAL_TAG_SCOPE };

export class TagScopeError extends Error {
  constructor() {
    super('tag_scope_unavailable');
    this.name = 'TagScopeError';
  }
}

export interface CustomerPanelPrivateNote {
  text: string;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}

export interface CustomerPanelResponse {
  contact: CustomerPanelContact;
  owner: CustomerPanelOwner | null;
  conversation: CustomerPanelConversation | null;
  tags: CustomerPanelTag[];
  /** 'personal' from a server with personal tags; absent from older servers (tags then hidden). */
  tagScope?: string;
  activity: {
    conversationsCount: number;
    avgReplyTimeSeconds: number | null;
  };
  privateNote: CustomerPanelPrivateNote | null;
}

export interface CustomerPanelProfilePatch {
  displayName?: string;
  email?: string;
  language?: string;
  city?: string;
  country?: string;
}

export interface CustomerPanelHistoryItem {
  ticketId: string;
  status: string;
  createdAt: string;
  resolvedAt: string | null;
  lastMessage: string | null;
  department: CustomerPanelDepartment | null;
  priority: CustomerPanelPriority;
}

/** The hand-off picker's row, from GET /api/agents/assignable: id + name +
 *  active only, never email/role/department/branch. `role` is tolerated for
 *  older mocks. WHICH rows come back is role-dependent — see
 *  listCustomerPanelAgents(). The list may legitimately be EMPTY. */
export interface CustomerPanelAgent {
  id: string;
  name: string;
  role?: string;
  active: boolean;
}

export function getCustomerPanel(
  customerId: string,
  ticketId?: string | null,
): Promise<CustomerPanelResponse> {
  const query = ticketId ? `?ticketId=${encodeURIComponent(ticketId)}` : '';
  return apiFetch<CustomerPanelResponse>(
    `/customers/${encodeURIComponent(customerId)}/panel${query}`,
  );
}

export function patchCustomerPanelProfile(
  customerId: string,
  patch: CustomerPanelProfilePatch,
): Promise<{ contact: Omit<CustomerPanelContact, 'sessionActive'> }> {
  return apiFetch(`/customers/${encodeURIComponent(customerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// The /my-tags paths exist only on servers with personal tags: during a mixed-version or rollback
// window an older server answers 404 there, so a "personal" create can never become a shared tag.
function personalTag(res: { scope?: unknown; tag?: unknown }): CustomerPanelTag {
  if (res?.scope !== PERSONAL_TAG_SCOPE || !res.tag) throw new TagScopeError();
  return res.tag as CustomerPanelTag;
}

/** The signed-in account's own tags. */
export async function listCustomerPanelTags(init: { signal?: AbortSignal } = {}): Promise<CustomerPanelTag[]> {
  const res = await apiFetch<{ scope?: unknown; tags?: unknown }>('/my-tags', init);
  if (res?.scope !== PERSONAL_TAG_SCOPE || !Array.isArray(res.tags)) throw new TagScopeError();
  return res.tags as CustomerPanelTag[];
}

/** Create a personal tag, or reuse this account's tag of the same name. */
export async function createCustomerPanelTag(
  name: string,
  color?: string,
): Promise<CustomerPanelTag> {
  return personalTag(await apiFetch('/my-tags', {
    method: 'POST',
    body: JSON.stringify({ name, ...(color ? { color } : {}) }),
  }));
}

/** Attach one of this account's own tags (by id, or created/reused by name) to a customer. */
export async function attachCustomerPanelTag(
  customerId: string,
  input: { tagId: string } | { name: string; color?: string },
): Promise<CustomerPanelTag> {
  return personalTag(await apiFetch(
    `/customers/${encodeURIComponent(customerId)}/my-tags`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  ));
}

/** Remove this account's own tag from a customer (other accounts' tags are never touched). */
export function detachCustomerPanelTag(
  customerId: string,
  tagId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/customers/${encodeURIComponent(customerId)}/my-tags/${encodeURIComponent(tagId)}`,
    { method: 'DELETE' },
  );
}

export function patchCustomerPanelTicketMeta(
  ticketId: string,
  patch: { departmentId?: string | null; priority?: CustomerPanelPriority },
): Promise<{ conversation: CustomerPanelConversation }> {
  return apiFetch(`/tickets/${encodeURIComponent(ticketId)}/meta`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function putCustomerPanelPrivateNote(
  customerId: string,
  text: string,
): Promise<{ privateNote: CustomerPanelPrivateNote }> {
  return apiFetch(`/customers/${encodeURIComponent(customerId)}/private-note`, {
    method: 'PUT',
    body: JSON.stringify({ text }),
  });
}

export function listCustomerPanelConversations(
  customerId: string,
): Promise<CustomerPanelHistoryItem[]> {
  return apiFetch<CustomerPanelHistoryItem[]>(
    `/customers/${encodeURIComponent(customerId)}/conversations`,
  );
}

export function listCustomerPanelAgents(): Promise<{ agents: CustomerPanelAgent[] }> {
  // The eligible hand-off targets for the CALLER, decided server-side:
  //   • admin / ordinary agent — every agent in the tenant, so a customer can
  //     still be handed to another department or branch;
  //   • supervisor — only agents sharing one of their assigned branches, PLUS
  //     active same-tenant admins as an escalation exception;
  //   • supervisor with NO assigned branches — an EMPTY list, and the write
  //     path refuses every target, including admins.
  // So an empty response is a normal, authorized result, not an error, and a
  // customer's current owner may legitimately be ABSENT from this list.
  //
  // This is NOT the management roster. GET /api/agents is branch-only for
  // supervisors with no admin escalation, and carries email, role, department,
  // branch and counts. The two endpoints have deliberately different rules;
  // never infer one's permissions from the other.
  //
  // The server is authoritative: the same eligibility is re-checked on the
  // hand-off write path, so a target absent here is refused there too.
  return apiFetch<{ agents: CustomerPanelAgent[] }>('/agents/assignable');
}
